import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import Avatar from "@mui/material/Avatar";
import "./style.css";
import { toast } from "react-toastify";
import ChatMessageInput from "./ChatMessageInput";
import { ImageLoader } from "../../../assets/Loader";
import { ReactComponent as Logout } from "../../../assets/images/ChatImages/logout.svg";
import { ReactComponent as HashTag } from "../../../assets/images/ChatImages/hashTags.svg";
import { ReactComponent as ChatBubble } from "../../../assets/images/ChatImages/chatbubble.svg";
import { ReactComponent as ChatEdit } from "../../../assets/images/ChatImages/chatedit.svg";
import { ReactComponent as ChatDelete } from "../../../assets/images/ChatImages/chatdelete.svg";
import { ReactComponent as LoaderMenu } from "../../../assets/images/ChatImages/loadingcircle.svg";
import { ReactComponent as AgentAvatar } from "../../../assets/images/avatar.svg"
import ChatMessageFooter from "./ChatMessageFooter";
import { sendMessageApi } from "../../../store/features/sendMessageAgent";
import { unArchivedRoomApi } from "../../../store/features/unArchivedRoom";
import { getRoomFeedbackById } from "../../../store/features/getRoomFeedbackById";
import { assigningRoom } from "../../../store/features/assigningRoom";
import { markRead } from "../../../store/features/markRead";
import { BASE_URL, ROOT_URL } from "../../../api/ApiConfig";
import { createAppSocket, emitAgentSetup } from '../../../api/createAppSocket';
import { ReactComponent as trustchatLogo } from "../../../assets/images/trustchatlogo.svg";
import { message } from "antd";
import { Skeleton } from "@mui/material";
import { ReactComponent as DoubleGreentick } from "../../../assets/images/ChatImages/doubleTick.svg";
import { ReactComponent as DoubleGreytick } from "../../../assets/images/ChatImages/doubleGreyTick.svg";
import { useNavigate } from "react-router-dom";
import { formatText } from "../../../utils/index";
import { chracterPosition } from "../../../utils/index";
import { colorCodesForAvatar } from "../../../constants/index";
import ChatMessageSkeleton from "./ChatMessageSkeleton";
import CustomTimeTooltip from "./CustomTimeTooltip";
import { msgTimeWithSeconds, visitorAppearsOnline } from "../../../utils/index";
import TypingIndicator from "./TypingIndicator";
import { ReactComponent as GreenDot } from "../../../assets/images/ChatImages/active-dot.svg";
import { ReactComponent as GreyDot } from "../../../assets/images/ChatImages/inactive-dot.svg";
import { formatAgentRoleLabel } from "../../../utils/agentMappers";
import { isJoinNoticeMessage } from "../../../utils/messageNotification";
import { getLiveChatBasePath } from "../../../utils/liveChatPath";
import { buildRoomSocketPayload } from "../../../utils/socketRoomId";
const ENDPOINT = `${BASE_URL}`;

let selectedChatCompare;

/**
 * Chat attachment image: keep the <img> in the layout so load/error always fire.
 * Hiding with display:none + loading="lazy" left the spinner stuck even when AWS
 * had already returned the bytes (visible in the network tab).
 */
function ChatAttachmentImage({ src, onOpen, fullWidth = false }) {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [src]);

  useEffect(() => {
    const el = imgRef.current;
    if (!el || !src) return;
    if (el.complete && el.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  return (
    <div
      className={`image-container${fullWidth ? " image-container--full" : ""}`}
      style={{
        position: "relative",
        width: fullWidth ? "100%" : undefined,
        minWidth: fullWidth ? 240 : undefined,
        minHeight: loaded ? undefined : 180,
      }}
    >
      {!loaded && <ImageLoader fullWidth={fullWidth} />}
      <a
        href={src}
        onClick={(e) => {
          e.preventDefault();
          onOpen(src);
        }}
        style={{
          display: "block",
          width: fullWidth ? "100%" : undefined,
          // Keep the request in-flight while the placeholder shows (never display:none)
          position: loaded ? "relative" : "absolute",
          inset: loaded ? undefined : 0,
          opacity: loaded ? 1 : 0,
          pointerEvents: loaded ? "auto" : "none",
          overflow: "hidden",
        }}
        aria-hidden={!loaded}
      >
        <img
          ref={imgRef}
          className="chat-attachment-image"
          style={{
            width: fullWidth ? "100%" : "180px",
            height: fullWidth ? "auto" : "180px",
            maxHeight: fullWidth ? "320px" : "180px",
            objectFit: "cover",
            display: "block",
            borderRadius: "8px",
          }}
          src={src}
          alt="Chat Attachment"
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
        />
      </a>
    </div>
  );
}

function pickEndedNotice(payload) {
  if (payload == null || typeof payload !== "object") return {};
  const endedMessage =
    payload.endedMessage ||
    payload.archived_message ||
    payload.feedback?.endedMessage ||
    payload.feedback?.archived_message ||
    payload.room?.archived_message ||
    payload.room?.endedMessage ||
    null;
  const endedByRole =
    payload.endedByRole || payload.feedback?.endedByRole || null;
  const endedBy = payload.endedBy || payload.feedback?.endedBy || null;
  return { endedMessage, endedByRole, endedBy };
}

function getAgentDisplayForMessage(firstItem, userData, chatIsUnassigned) {
  const currentUser = userData?.response?.user;
  const currentUserId = currentUser?._id;
  // Only fall back to the logged-in user's profile for their own messages.
  // Otherwise admin view would paint every staff message with the admin avatar.
  const isOwnMessage =
    currentUserId != null &&
    [firstItem?.mainUserId, firstItem?.agent?._id, firstItem?.agent?.id].some(
      (id) => id != null && String(id) === String(currentUserId)
    );

  // `role_name` comes with the agent on each message, so it is the exact role of
  // that message's sender. The logged-in user's own role is only a valid fallback
  // for their own messages.
  const role =
    firstItem?.agent?.role_name ||
    firstItem?.agent?.role ||
    (isOwnMessage ? currentUser?.role_name : "") ||
    "";

  if (chatIsUnassigned) {
    const name = (
      firstItem?.sender_name ||
      firstItem?.agent?.full_name ||
      ""
    ).trim();
    return {
      name: name || "Agent",
      photo: null,
      letter: "",
      role,
    };
  }

  const name = (
    firstItem?.sender_name ||
    firstItem?.agent?.full_name ||
    (isOwnMessage ? currentUser?.full_name : "") ||
    ""
  ).trim();
  const photo =
    firstItem?.agent?.agent_image ||
    (isOwnMessage ? currentUser?.agent_image : null) ||
    null;
  const letter = name ? name.charAt(0).toUpperCase() : "";
  return { name, photo, letter, isOwnMessage, role };
}

/**
 * Widget auto-welcome / system messages render on the agent side but are not
 * sent by a person, so they carry no sender identity and have no profile to show.
 *
 * @param {Record<string, any> | null | undefined} item
 * @returns {boolean}
 */
function isSystemAgentMessage(item) {
  if (!item) return false;
  if (item.isWelcome === true || item.isSystem === true) return true;
  const hasSenderIdentity =
    Boolean(item.mainUserId) ||
    Boolean(item.agent?._id) ||
    Boolean(item.agent?.id) ||
    Boolean((item.sender_name || "").trim());
  return !hasSenderIdentity;
}

function ChatMessageComponent(props) {
  const [drafts, setDrafts] = useState({});
  const [messages, setMessages] = useState([]);
  const [endChat, setEndChat] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState("");
  const [isIssueResolved, setIsIssueResolved] = useState(false);
  const [endChatTime, setEndChatTime] = useState("");
  const [endRoomTime, setEndRoomTime] = useState("");

  const [showbubbleMenu, setShowBubbleMenu] = useState(true);
  const [editedMessage, setEditedMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const [socketConnected, setSocketConnected] = useState(false);
  const [socketInstance, setSocketInstance] = useState(null);
  const [showTimestamp, setShowTimestamp] = useState(false);
  const [cannedMenu, setCannedMenu] = useState(null);
  const [inputText, setInputText] = useState("");
  const [fileImage, setFileImage] = useState(null);
  const [fileImageSrc, setFileImageSrc] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState("");

  const dispatch = useDispatch();
  const userData = useSelector((state) => state.loginToken);
  const agentData = useSelector((state) => state.loginTokenCoUser);
  const socketRef = useRef(null);
  const navigate = useNavigate();

  const {
    selectUser,
    setSelectUser,
    unArchivedRoom,
    setChatSideBar,
    setCannedResponse,
    setCannedMsg,
    getRoomId,
    setGetRoomId,
    assignedAgentImage,
    typingIndicator,
    setTypingIndicator,
    triggerOpenRoom,
    onRoomAssigned,
    loading,
    variant,
  } = props;
  const isV2 = variant === "v2";
  // console.log("getRoomId",getRoomId, 'selectUser', selectUser);
  const dropdownRef = useRef(null);
  const messageContainerRef = useRef(null);
  const BootstrapTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.black,
    },
  }));

  const handleEditMessage = (message) => {
    setEditedMessage(message);
  };
  const [hoveredMessageIndex, setHoveredMessageIndex] = useState(null);

  const handleMouseEnter = (index) => {
    setHoveredMessageIndex(index);
  };

  const handleMouseLeave = () => {
    setHoveredMessageIndex(null);
  };

  const openFullScreen = (src) => {
    setModalImageSrc(src);
    setModalOpen(true);
  };
  const closeFullScreen = () => {
    setModalOpen(false);
  };

  const [hoveredAgentGroup, setHoveredAgentGroup] = useState(null);
  const [profileCardBelow, setProfileCardBelow] = useState(false);
  const selectUserIdRef = useRef(selectUser?.id);
  selectUserIdRef.current = selectUser?.id;
  const selectUserRef = useRef(selectUser);
  selectUserRef.current = selectUser;

  const [soundOn] = useState(true);
  const notificationSound = new Audio(
    "/sound/notification.mp3"
  );

  const showNotification = useCallback(
    (message) => {
      if (
        !document.hasFocus() &&
        "Notification" in window &&
        Notification.permission === "granted"
      ) {
        const notificationOptions = {
          body: message.content,
          icon: trustchatLogo,
          badge: trustchatLogo,
          tag: message.roomId,
          data: {
            roomId: message.roomId,
            messageId: message._id,
          },
        };
        const notification = new Notification(
          message.sender_name || "New Message",
          notificationOptions
        );

        notification.onclick = function (event) {
          event.preventDefault();
          window.focus();
          if (this.data && this.data.roomId) {
            navigate(`${getLiveChatBasePath(window.location.pathname)}?roomId=${this.data.roomId}`);
            if (selectUser.id === this.data.roomId) {
              const messageElement = document.getElementById(
                `message-${this.data.messageId}`
              );
              if (messageElement) {
                messageElement.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
                messageElement.classList.add("highlight-message");
                setTimeout(() => {
                  messageElement.classList.remove("highlight-message");
                }, 2000);
              }
            }
          }
          notification.close();
        };
      }
    },
    [selectUser.id, navigate]
  );

  // The connection is kept alive across room switches. Recreating it whenever
  // selectUser changed dropped the "join chat" room membership (that emit only
  // runs on room id change), so visitor messages stopped reaching the open chat.
  useEffect(() => {
    const s = createAppSocket();
    socketRef.current = s;

    const joinCurrentRoom = () => {
      emitAgentSetup(s);
      if (userData?.response?.user?.business_id) {
        s.emit("join_business", {
          businessId: userData.response.user.business_id,
        });
      }
      const roomId = selectUserIdRef.current;
      if (roomId) s.emit("join chat", roomId);
    };

    const onConnect = () => {
      joinCurrentRoom();
    };

    const onConnected = () => {
      setSocketConnected(true);
      console.log("Connected to", ENDPOINT);
    };

    s.on("connect", onConnect);
    s.on("connected", onConnected);
    joinCurrentRoom();
    setSocketInstance(s);

    return () => {
      s.emit("stop-typing", buildRoomSocketPayload(selectUserRef.current));
      s.off("connect", onConnect);
      s.off("connected", onConnected);
      s.disconnect();
      if (socketRef.current === s) socketRef.current = null;
      setSocketInstance((prev) => (prev === s ? null : prev));
      setSocketConnected(false);
      setTypingIndicator(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.response?.user?._id]);

  useEffect(() => {
    const socketInstanceLocal = socketInstance;
    if (!socketInstanceLocal) return undefined;

    const onEndChat = (feedbackData) => {
      const roomId = resolveSocketRoomId(feedbackData);
      if (!roomId || String(roomId) !== String(selectUser.id)) return;
      const notice = pickEndedNotice(feedbackData);
      const ratingValue =
        feedbackData?.rating ?? feedbackData?.feedback?.rating ?? null;
      const issueResolvedValue =
        feedbackData?.issueResolved ??
        feedbackData?.feedback?.issueResolved ??
        null;
      setEndChat(true);
      setFeedback(ratingValue);
      setEndChatTime(
        feedbackData?.time || feedbackData?.feedback?.time || notice.time,
      );
      if (typeof issueResolvedValue === "boolean") {
        setIsIssueResolved(issueResolvedValue);
      }
      setSelectUser((prev) =>
        prev && String(prev.id) === String(selectUser.id)
          ? {
              ...prev,
              end_session: true,
              endSession: true,
              endSessionTime:
                prev.endSessionTime ||
                feedbackData?.time ||
                new Date().toISOString(),
              isOnline: false,
              isLive: false,
              ...(notice.endedMessage
                ? { archived_message: notice.endedMessage }
                : {}),
              ...(notice.endedByRole ? { endedByRole: notice.endedByRole } : {}),
              ...(notice.endedBy ? { endedBy: notice.endedBy } : {}),
            }
          : prev
      );
    };

    const resolveSocketRoomId = (payload) => {
      if (payload == null || payload === "") return null;
      if (typeof payload === "string" || typeof payload === "number") {
        return String(payload);
      }
      const id =
        payload.roomId ?? payload.room_id ?? payload._id ?? payload.id ?? null;
      return id != null && id !== "" ? String(id) : null;
    };

    const markOpenChatEnded = (payload, { archived = false } = {}) => {
      const roomId = resolveSocketRoomId(payload);
      if (!roomId || String(roomId) !== String(selectUser.id)) return;

      const notice = pickEndedNotice(payload);

      setSelectUser((prev) =>
        prev && String(prev.id) === String(roomId)
          ? {
              ...prev,
              end_session: true,
              endSession: true,
              endSessionTime: prev.endSessionTime || new Date().toISOString(),
              ...(notice.endedMessage
                ? { archived_message: notice.endedMessage }
                : {}),
              ...(notice.endedByRole ? { endedByRole: notice.endedByRole } : {}),
              ...(notice.endedBy ? { endedBy: notice.endedBy } : {}),
              ...(archived ? { is_archived: true } : {}),
              isOnline: false,
              isLive: false,
            }
          : prev
      );
    };

    const onEndRoom = (payload) => markOpenChatEnded(payload);
    const onArchiveChat = (payload) =>
      markOpenChatEnded(payload, { archived: true });

    const onTyping = (r) => {
      if (r?._id === selectUser?.id && !selectUser?.end_session) {
        setTypingIndicator(`${r.roomName} is typing`);
      } else {
        setTypingIndicator(false);
      }
    };

    const onNotTyping = () => {
      setTypingIndicator(false);
    };

    const appendIncomingMessage = (incoming) => {
      if (!incoming) return;
      if (String(incoming.roomId) !== String(selectUser.id)) return;

      const loginUserId = userData?.response?.user._id;
      if (
        soundOn &&
        loginUserId === selectUser?.assignedTo &&
        !incoming.isPrivate &&
        !incoming.isAgent
      ) {
        // notificationSound.play().catch((error) => {
        //   console.error("Error playing sound:", error);
        // });
      }

      // Visitor activity in the open thread must match list presence (green dot).
      if (!incoming.isAgent && !incoming.isPrivate) {
        setSelectUser((prev) => {
          if (!prev || String(prev.id) !== String(selectUser.id)) return prev;
          if (prev.end_session || prev.isLive) return prev;
          return { ...prev, isLive: true, isOnline: true };
        });
      }

      setGetRoomId((prevMessages) => {
        const list = Array.isArray(prevMessages) ? prevMessages : [];
        const incomingId = incoming._id ?? incoming.tempId;
        const alreadyListed = list.some((msg) => {
          if (incomingId != null && (msg?._id != null || msg?.tempId != null)) {
            return (
              String(msg._id ?? msg.tempId) === String(incomingId)
            );
          }
          // Socket payloads emitted before the message is persisted carry no id.
          return (
            !!msg &&
            msg.content === incoming.content &&
            !!msg.isAgent === !!incoming.isAgent &&
            String(msg.createdAt ?? "") === String(incoming.createdAt ?? "")
          );
        });
        if (alreadyListed) return list;
        return [...list, incoming];
      });
    };

    const onMessageReceived = (newMessageReceived) => {
      appendIncomingMessage(newMessageReceived?.message);
    };


    const onMessageFetched = (newMessageReceived) => {
      const incoming = newMessageReceived?.message;
      if (!incoming || incoming.isAgent) return;
      appendIncomingMessage(incoming);
    };

    const onResetCount = (roomId) => {
      if (String(roomId) !== String(selectUser.id)) return;

      const visitorId =
        selectUser?.customerId ||
        (Array.isArray(selectUser?.roomUsers)
          ? selectUser.roomUsers.find(
              (id) => String(id) !== String(userData?.response?.user?._id),
            )
          : null);

      setGetRoomId((prevMessages) =>
        prevMessages.map((msg) => {
          if (!msg?.isAgent || msg?.isPrivate) return msg;

          const alreadyRead =
            (Array.isArray(msg.readBy) && msg.readBy.length > 0) ||
            (Array.isArray(msg.readBy) &&
              msg.readBy.length === 0 &&
              Array.isArray(msg.unreadBy) &&
              msg.unreadBy.length === 0);

          if (alreadyRead) return msg;

          const nextReadBy = Array.isArray(msg.readBy)
            ? [...msg.readBy]
            : [];
          if (visitorId && !nextReadBy.some((id) => String(id) === String(visitorId))) {
            nextReadBy.push(visitorId);
          }
          if (nextReadBy.length === 0) {
            nextReadBy.push(visitorId || "read");
          }

          return {
            ...msg,
            readBy: nextReadBy,
            unreadBy: [],
          };
        }),
      );
    };

    const onVisitorStatus = ({ userId, isActive, userType }) => {
      if (userType !== "customer" || userId == null) return;
      const statusUserId = String(userId);
      const nextLive = !!isActive;
      const openRoomId = String(selectUserIdRef.current || selectUser.id || "");
      setSelectUser((prev) => {
        if (!prev || String(prev.id) !== openRoomId) return prev;
        if (prev.end_session) return prev;
        const matches =
          String(prev.customerId) === statusUserId ||
          (Array.isArray(prev.roomUsers) &&
            prev.roomUsers.some((id) => String(id) === statusUserId));
        if (!matches || !!prev.isLive === nextLive) return prev;
        return {
          ...prev,
          isLive: nextLive,
          isOnline: isActive ? prev.isOnline : false,
        };
      });
    };

    socketInstanceLocal.on("end-chat", onEndChat);
    socketInstanceLocal.on("end-room", onEndRoom);
    socketInstanceLocal.on("archive-chat", onArchiveChat);
    socketInstanceLocal.on("is-typing", onTyping);
    socketInstanceLocal.on("not-typing", onNotTyping);
    socketInstanceLocal.on("message received", onMessageReceived);
    socketInstanceLocal.on("msg fetched", onMessageFetched);
    socketInstanceLocal.on("reset-count", onResetCount);
    socketInstanceLocal.on("update_user_status", onVisitorStatus);

    return () => {
      socketInstanceLocal.off("end-chat", onEndChat);
      socketInstanceLocal.off("end-room", onEndRoom);
      socketInstanceLocal.off("archive-chat", onArchiveChat);
      socketInstanceLocal.off("is-typing", onTyping);
      socketInstanceLocal.off("not-typing", onNotTyping);
      socketInstanceLocal.off("message received", onMessageReceived);
      socketInstanceLocal.off("msg fetched", onMessageFetched);
      socketInstanceLocal.off("reset-count", onResetCount);
      socketInstanceLocal.off("update_user_status", onVisitorStatus);
    };
  }, [
    socketInstance,
    selectUser.id,
    selectUser?.assignedTo,
    selectUser?.customerId,
    selectUser?.roomUsers,
    selectUser?.end_session,
    soundOn,
    userData?.response?.user?._id,
    setGetRoomId,
    setSelectUser,
    setTypingIndicator,
  ]);

  useEffect(() => {
    if (!socketInstance || !selectUser.id) return undefined;
    const joinedRoom = selectUser;
    socketInstance.emit("join chat", selectUser.id);
    setInputText("");
    if (selectUser.end_session || selectUser.is_archived) {
      getRoomFeedback();
    }

    return () => {
      socketInstance.emit("stop-typing", buildRoomSocketPayload(joinedRoom));
      setTypingIndicator(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketInstance, selectUser.id]);

  useEffect(() => {
    // Reset ended/feedback UI when switching rooms so stale visitor feedback
    // from a previous chat cannot appear on a newly closed room.
    setEndChat(false);
    setFeedback("");
    setRating("");
    setIsIssueResolved(false);
    setEndChatTime("");
    setEndRoomTime("");
  }, [selectUser.id]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setCannedMenu(false);
        setShowBubbleMenu(false);
      }
    };

    if (cannedMenu || showbubbleMenu) {
      window.addEventListener("click", handleOutsideClick);
    }

    return () => {
      window.removeEventListener("click", handleOutsideClick);
    };
  }, [cannedMenu, showbubbleMenu]);

  useEffect(() => {
    if (drafts[selectUser.id]) {
      setInputText(drafts[selectUser.id]);
    } else {
      setInputText("");
    }
  }, [selectUser.id, drafts]);

  const sendMessage = (user, text) => {
    if (selectUser) {
      const timestamp = new Date();
      if (isEditing) {
        const editedMessages = messages.map((message) =>
          message.id === editedMessage.id
            ? { ...message, text, timestamp }
            : message
        );
        setMessages(editedMessages);
        setEditedMessage("");
        setIsEditing(false);
      } else {
        const newMessage = {
          id: new Date(),
          user: selectUser,
          text,
          timestamp,
        };
        setMessages((prevMessages) => [...prevMessages, newMessage]);

        setShowTimestamp(true);
      }
    }
  };

  const [isPrivate, setIsPrivate] = useState(false);

  const currentUserId = userData?.response?.user?._id;
  /** Only the assigned agent may send a public reply; others are limited to internal notes. */
  const canSendPublicReply = useMemo(() => {
    const assigned = selectUser?.assignedTo;
    if (assigned == null || assigned === "") return true;
    const assigneeId =
      typeof assigned === "object"
        ? assigned._id ?? assigned.id ?? null
        : assigned;
    if (!assigneeId) return true;
    return (
      currentUserId != null && String(assigneeId) === String(currentUserId)
    );
  }, [selectUser?.assignedTo, currentUserId]);

  useEffect(() => {
    if (!canSendPublicReply) {
      setIsPrivate(true);
    }
  }, [canSendPublicReply, selectUser?.id]);

  const sendMessageHandler = async () => {
    const mainUserId = userData?.response?.user._id;
    const mainUserName = userData?.response?.user?.full_name;
    const business_id = userData?.response?.user?.business_id;
    const agent_image = userData?.response?.user?.agent_image;
    const now = new Date();
    // Match server/mongoose trim so optimistic bubbles don't keep trailing
    // newlines (white-space: pre-wrap) that disappear after the API response.
    const content = String(inputText ?? "").trim();
    const effectiveIsPrivate = canSendPublicReply ? isPrivate : true;

    const visitorId = selectUser?.customerId;
    const tempId = `temp_${Date.now()}`;
    const optimisticMessage = {
      _id: tempId,
      tempId,
      mainUserId: mainUserId,
      sender_name: mainUserName,
      content,
      file_image: fileImageSrc,
      roomId: selectUser.id,
      business_id: business_id,
      isAgent: true,
      isPrivate: effectiveIsPrivate,
      agent: { agent_image },
      createdAt: now,
      readBy: [],
      unreadBy: visitorId ? [visitorId] : [],
    };

    setGetRoomId((prevMessages) => [...prevMessages, optimisticMessage]);

    setInputText("");
    setFileImage("");
    setFileImageSrc("");

    socketRef.current?.emit("new message", { message: optimisticMessage });

    // Prepare form data for API call
    const formData = new FormData();
    formData.append("mainUserId", mainUserId);
    formData.append("sender_name", mainUserName);
    formData.append("content", content);
    formData.append("file_image", fileImage);
    formData.append("roomId", selectUser.id);
    formData.append("business_id", business_id);
    formData.append("isAgent", true);
    formData.append("isPrivate", effectiveIsPrivate);
    formData.append("createdAt", now);

    try {
      const response = await dispatch(sendMessageApi(formData));
      socketRef.current?.emit("stop-typing", buildRoomSocketPayload(selectUser));

      if (!response.payload) {
        toast.error("Some Error Occurred. Please try again later.");
      } else if (response?.payload?.status === 200) {
        const serverMessage = response?.payload?.message;
        if (serverMessage) {
          setGetRoomId((prevMessages) =>
            prevMessages.map((msg) => {
              if (msg.tempId !== tempId && msg._id !== tempId) return msg;

              // Widget may have already emitted reset-count before this API
              // response arrives — keep ticks green instead of reverting to unread.
              const alreadyReadLocally =
                (Array.isArray(msg.readBy) && msg.readBy.length > 0) ||
                (Array.isArray(msg.readBy) &&
                  msg.readBy.length === 0 &&
                  Array.isArray(msg.unreadBy) &&
                  msg.unreadBy.length === 0);

              const serverReadBy = Array.isArray(serverMessage.readBy)
                ? serverMessage.readBy
                : [];
              const serverUnreadBy = Array.isArray(serverMessage.unreadBy)
                ? serverMessage.unreadBy
                : visitorId
                  ? [visitorId]
                  : [];

              const alreadyReadOnServer =
                serverReadBy.length > 0 ||
                (serverReadBy.length === 0 && serverUnreadBy.length === 0);

              if (alreadyReadLocally || alreadyReadOnServer) {
                return {
                  ...serverMessage,
                  readBy:
                    serverReadBy.length > 0
                      ? serverReadBy
                      : msg.readBy?.length
                        ? msg.readBy
                        : [visitorId || "read"],
                  unreadBy: [],
                };
              }

              return {
                ...serverMessage,
                readBy: serverReadBy,
                unreadBy: serverUnreadBy,
              };
            }),
          );
        }
      } else if (response?.payload?.status === 500) {
        toast.error("Some Error Occurred. Please try again later.");
        setGetRoomId((prevMessages) =>
          prevMessages.filter((msg) => msg.tempId !== tempId && msg._id !== tempId),
        );
      }
    } catch (error) {
      toast.error(error.message || "Failed to send message");
      setGetRoomId((prevMessages) =>
        prevMessages.filter((msg) => msg.tempId !== tempId && msg._id !== tempId),
      );
    }
  };

  const handleDeleteMessage = (messageId) => {
    const updatedMessages = messages.filter(
      (message) => message.id !== messageId
    );
    console.log("update message function", updatedMessages);
    setMessages(updatedMessages);
  };

  useEffect(() => {
    const el = messageContainerRef.current;
    if (!el) return;
    // While the skeleton is shown, getRoomId is often cleared first — scrolling to
    // scrollHeight then pins the view to the bottom of short placeholder content.
    if (loading) {
      el.scrollTop = 0;
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, [messages, getRoomId, loading]);

  const formatTimestamp = (timestamp) =>
    timestamp.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

  const openCannedMenu = (id) => {
    setCannedMenu(id);
  };
  function shouldDisplayTime(currentTimestamp, previousTimestamp) {
    const oneHour = 60 * 60 * 1000; // Number of milliseconds in one hour

    // Convert timestamps to Date objects
    const currentDate = new Date(currentTimestamp);
    const previousDate = new Date(previousTimestamp);

    const timeDifferenceInHours = (currentDate - previousDate) / oneHour;

    const differentDay =
      currentDate.toDateString() !== previousDate.toDateString();

    return timeDifferenceInHours >= 12 || differentDay;
  }

  const chatStartedRaw =
    selectUser?.startTime ||
    (Array.isArray(getRoomId) && getRoomId.length > 0
      ? getRoomId[0]?.createdAt
      : null);
  const date = chatStartedRaw ? new Date(chatStartedRaw) : null;
  const hasValidStartTime = date && !Number.isNaN(date.getTime());
  let formattedTime = "";
  if (hasValidStartTime) {
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const amOrPm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const formattedHours = hours.toString().padStart(2, "0");
    const formattedMinutes = minutes.toString().padStart(2, "0");
    formattedTime = `${formattedHours}:${formattedMinutes} ${amOrPm}`;
  }

  const msgTime = (time) => {
    const date = new Date(time);
    if (Number.isNaN(date.getTime())) return "";
    const formattedTime = date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    // Figma uses lowercase am/pm (e.g. "01:34 pm")
    return isV2 ? formattedTime.toLowerCase() : formattedTime;
  };
  // console.log("Messages here", getRoomId);
  // console.log("Messages of time", getRoomId);

  const handleOpenChat = () => {
    if (selectUser) {
      const unArcRoom = dispatch(
        unArchivedRoomApi({
          id: selectUser.id,
        })
      );
      unArcRoom
        .then(async (data) => {
          unArchivedRoom();
          setSelectUser(null);
          if (typeof data.payload === "undefined") {
            toast.error("Some Error Occured. Please try again later.");
          } else if (data?.payload?.status === 200) {
          } else if (data?.payload?.status === 500) {
            toast.error("Some Error Occured. Please try again later.");
          } else if (data?.payload?.status === 401) {
            toast.error("Some Error Occured. Please try again later.");
          }
        })
        .catch(async (error) => {
          toast.error(error);
        });
    }
  };

  const handleOpenRoom = async () => {
    if (selectUser) {
      setIsAssigning(true);

      try {
        const response = await dispatch(
          assigningRoom({
            requestHeaders: {
              mainUserId: userData?.response?.user._id,
              roomId: selectUser?.id,
            },
          })
        );

        if (response?.payload?.success === true) {
          triggerOpenRoom();
          onRoomAssigned?.(selectUser.id);
          setIsAssigning(false);
          setChatSideBar(true);
          socketRef.current?.emit("self-assign", selectUser.id);
          const userId = userData?.response?.user?._id;

          socketRef.current?.emit("mark-read", selectUser.id);
          await dispatch(
            markRead({
              requestHeaders: {
                roomId: selectUser.id,
                userId: userId,
              },
            })
          );

          toast.success("Chat Room Assigned Successfully");
          navigate(`${getLiveChatBasePath(window.location.pathname)}?roomId=${selectUser.id}`);
        } else {
          toast.error(response?.payload?.message || "Failed to assign chat");
        }
      } catch (error) {
        triggerOpenRoom();
        toast.error(error.message || "Failed to assign chat");
      } finally {
        setIsAssigning(false);
      }
    }
  };

  const TIME_THRESHOLD = 15 * 60 * 1000;

  const getRoomFeedback = () => {
    const requestedRoomId = selectUser.id;
    const getFeedback = dispatch(
      getRoomFeedbackById({
        id: requestedRoomId,
      })
    );
    getFeedback
      .then(async (data) => {
        if (String(selectUserIdRef.current) !== String(requestedRoomId)) return;
        if (typeof data.payload === "undefined") {
          toast.error("Some Error Occured. Please try again later.");
        } else if (data?.payload?.status === "success") {
          const fb = data?.payload?.feedback?.feedback;
          const endSessionTime = data?.payload?.feedback?.end_session_time;
          const notice = pickEndedNotice(data?.payload?.feedback || data?.payload);
          const endedMessage = notice.endedMessage;
          console.log(
            "data?.payload?.feedback?.rating",
            fb?.rating
          );
          // Only apply feedback when the visitor actually submitted it.
          setRating(fb?.rating || "");
          setFeedback(fb?.rating || "");
          setIsIssueResolved(
            typeof fb?.issueResolved === "boolean" ? fb.issueResolved : false
          );
          setEndRoomTime(endSessionTime || "");
          if (endSessionTime || fb?.rating || endedMessage) {
            setSelectUser((prev) =>
              prev && String(prev.id) === String(requestedRoomId)
                ? {
                    ...prev,
                    endSessionTime: prev.endSessionTime || endSessionTime,
                    ...(endedMessage
                      ? { archived_message: endedMessage }
                      : {}),
                    ...(notice.endedByRole
                      ? { endedByRole: notice.endedByRole }
                      : {}),
                    ...(fb?.rating
                      ? {
                          rating: fb.rating,
                          isIssueResolved:
                            typeof fb?.issueResolved === "boolean"
                              ? fb.issueResolved
                              : prev.isIssueResolved,
                        }
                      : {}),
                  }
                : prev
            );
          }
        } else {
          toast.error("Some Error Occured. Please try again later.");
        }
      })
      .catch(async (error) => {
        toast.error(error);
      });
  };

  // function isFirstInGroup(messages, index, keysToCompare, timeThresholdMs = 60000) {
  //   if (index === 0) return true;
  //   const prev = messages[index - 1];
  //   const current = messages[index];
  //   if (!prev || !current) return true;
  //   const isDifferentByKey = keysToCompare.some(key => prev[key] !== current[key]);
  //   console.log("isDifferentByKey", isDifferentByKey);
  //   const prevTime = new Date(prev.timestamp || prev.createdAt).getTime();
  //   const currentTime = new Date(current.timestamp || current.createdAt).getTime();
  //   const isTooFarApart = currentTime - prevTime > timeThresholdMs;
  //   return isDifferentByKey
  // }

  // The backend records "<visitor> has joined the chat" as a real message, but
  // it is room status rather than something anyone said, so it is kept out of
  // the thread instead of rendering as a bubble above the visitor's first line.
  const visibleMessages = Array.isArray(getRoomId)
    ? getRoomId.filter((item) => !isJoinNoticeMessage(item))
    : [];

  // Group by sender and message type only (no time threshold)
  function groupMessagesBySenderAndType(messages) {
    console.log("messages", messages);
    if (!messages.length) return [];
    const groups = [];
    let currentGroup = [messages[0]];
    for (let i = 1; i < messages.length; i++) {
      const prev = messages[i - 1];
      const curr = messages[i];
      const sameSender =
        prev.isAgent === curr.isAgent &&
        prev.sender_name === curr.sender_name &&
        prev.isPrivate === curr.isPrivate;

      console.log("is same sender", sameSender);
      if (sameSender) {
        currentGroup.push(curr);
      } else {
        groups.push(currentGroup);
        currentGroup = [curr];
      }
    }
    if (currentGroup.length) groups.push(currentGroup);
    return groups;
  }
  

  return (
    <div className={`ChatMessageComponent-Container${isV2 ? " chatv2-messages" : ""}`}>

      <div className="MessageComponet-Main">
        <div className="MessageComponent-Main-02" ref={messageContainerRef}>
          <div className="Message-Time-Header" style={{ marginBottom: isV2 ? "24px" : "40px" }}>
            <span className="Left-Line" />
            <p className="Message-Time-Text">
              {isV2 ? (
                <>
                  <span className="cv2-chat-started-label">Chat started:</span>
                  <span className="cv2-chat-started-time">
                    {(formattedTime || "--").toLowerCase()}
                  </span>
                </>
              ) : (
                <>
                  Chat started:
                  {formattedTime || "--"}
                </>
              )}
            </p>
            <span className="right-Line" />
          </div>
          {/* Only show skeleton if loading is true */}
          {loading ? (
            <ChatMessageSkeleton variant={isV2 ? "v2" : undefined} />
          ) : (
            groupMessagesBySenderAndType(visibleMessages).map((group, groupIdx) => {
              const firstItem = group[0];
              console.log("firstItem", firstItem);
              const isAgentGroup = firstItem.isAgent;
              const chatIsUnassigned =
                selectUser?.is_unAssigned && !selectUser?.is_archived;
              // No agent profile card for system/welcome messages.
              const isSystemGroup = isSystemAgentMessage(firstItem);
              const agentDisplay = getAgentDisplayForMessage(
                firstItem,
                userData,
                chatIsUnassigned
              );
              return (
                <div
                  key={groupIdx}
                  className="message-group-block"
                  style={{
                    display: "flex",
                    flexDirection: isAgentGroup ? "row-reverse" : "row",
                    alignItems: "flex-start",
                  }}
                >
                  {/* Avatar only once per group, on correct side */}
                  <div
                    className={isV2 ? "chatv2-avatar-wrap" : undefined}
                    style={{ margin: isAgentGroup ? "0 0 0 8px" : "0 8px 0 0" }}
                  >
                    <div className={`avatarContainer${isV2 ? " chatv2-avatar" : ""}`}>
                      {isAgentGroup ? (
                        <div
                          className={isV2 ? "chatv2-avatar-inner" : undefined}
                          style={{ position: "relative", width: 32, height: 32 }}
                          onMouseEnter={(e) => {
                            if (chatIsUnassigned || isSystemGroup) return;
                            const cardHeight = 104;
                            const containerRect = messageContainerRef.current?.getBoundingClientRect();
                            const avatarRect = e.currentTarget.getBoundingClientRect();
                            const visibleTop = containerRect?.top || 0;
                            const willClipTop = (avatarRect.top - (cardHeight + 8)) < visibleTop;
                            const forceBelow = groupIdx === 0 || !!firstItem?.file_image;
                            setProfileCardBelow(forceBelow || willClipTop);
                            setHoveredAgentGroup(groupIdx);
                          }}
                          onMouseLeave={() => {
                            setHoveredAgentGroup(null);
                            setProfileCardBelow(false);
                          }}
                        >
                          {agentDisplay.photo ? (
                            <Avatar
                              src={agentDisplay.photo}
                              sx={{ width: 32, height: 32 }}
                            />
                          ) : agentDisplay.letter ? (
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor:
                                  colorCodesForAvatar[
                                    chracterPosition(agentDisplay.letter)
                                  ],
                              }}
                            >
                              {agentDisplay.letter}
                            </Avatar>
                          ) : (
                              <AgentAvatar />
                          )}
                          {!chatIsUnassigned && !isSystemGroup && hoveredAgentGroup === groupIdx && (
                            <div
                              className="chatv2-agent-card"
                              style={{
                                position: "absolute",
                                right: 0,
                                top: profileCardBelow ? 50 : "auto",
                                bottom: profileCardBelow ? "auto" : 40,
                                width: 288,
                                height: 104,
                                borderRadius: 8,
                                border: "1px solid #D5DCE1",
                                background: "#FFF",
                                boxShadow: "0 15px 20px 0 rgba(6, 21, 34, 0.05)",
                                zIndex: 20,
                                display: "flex",
                                alignItems: "center",
                                padding: 16,
                                gap: 12,
                              }}
                            >
                              {agentDisplay.photo ? (
                                <img
                                  src={agentDisplay.photo}
                                  alt="Agent"
                                  style={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    flexShrink: 0,
                                  }}
                                />
                              ) : agentDisplay.letter ? (
                                <Avatar
                                  sx={{
                                    width: 72,
                                    height: 72,
                                    bgcolor:
                                      colorCodesForAvatar[
                                        chracterPosition(agentDisplay.letter)
                                      ],
                                  }}
                                >
                                  {agentDisplay.letter}
                                </Avatar>
                              ) : (
                                  <AgentAvatar style={{marginBottom: "12px"}} />
                              )}
                              <div style={{ display: "flex", flexDirection: "column" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <div
                                    style={{
                                      color: "#061522",
                                      fontSize: 14,
                                      fontStyle: "normal",
                                      fontWeight: 500,
                                      lineHeight: "18px",
                                    }}
                                  >
                                    {agentDisplay.name ||
                                      firstItem?.agent?.full_name ||
                                      (agentDisplay.isOwnMessage
                                        ? userData?.response?.user?.full_name
                                        : "") ||
                                      "Agent"}
                                  </div>
                                  <span
                                    style={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      background: "#389E0D",
                                      flexShrink: 0,
                                    }}
                                  />
                                </div>
                                <div
                                  style={{
                                    color: "#556575",
                                    fontSize: 12,
                                    fontStyle: "normal",
                                    fontWeight: 400,
                                    lineHeight: "18px",
                                  }}
                                >
                                  {agentDisplay.role
                                    ? formatAgentRoleLabel(agentDisplay.role)
                                    : ""}
                                </div>
                                <div
                                  style={{
                                    color: "#556575",
                                    fontSize: 12,
                                    fontStyle: "normal",
                                    fontWeight: 400,
                                    lineHeight: "18px",
                                    marginTop: "12px",
                                  }}
                                >
                                  {firstItem?.agent?.email ||
                                    (agentDisplay.isOwnMessage
                                      ? userData?.response?.user?.email
                                      : "") ||
                                    ""}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className={isV2 ? "chatv2-avatar-inner" : undefined} style={{ position: "relative", width: 32, height: 32 }}>
                          <Avatar
                            variant="circular"
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              bgcolor:
                                colorCodesForAvatar[
                                  chracterPosition(
                                    selectUser?.title?.substring(0, 1)?.toUpperCase()
                                  )
                                ],
                            }}
                          >
                            {selectUser?.title?.substring(0, 1).toUpperCase()}
                          </Avatar>
                          <div className={isV2 ? "chatv2-status-dot" : undefined} style={{ position: "absolute", right: "0px", bottom: "-4px" }}>
                            {visitorAppearsOnline(selectUser) ? <GreenDot /> : <GreyDot />}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Message bubbles stacked */}
                  <div
                    className={isV2 ? "chatv2-bubbles-col" : undefined}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: isAgentGroup ? "flex-end" : "flex-start",
                      marginBottom: isV2 ? "0" : "6px",
                      gap: isV2 ? "4px" : undefined,
                    }}
                  >
                    {group.map((item, idx) => {
                      const isLastInGroup = idx === group.length - 1;
                      const isFirstMessageOverall = groupIdx === 0 && idx === 0;
                      let messageBubbleClass = item.isAgent
                        ? "Img-Content-Agent"
                        : "Img-Content";
                      if (item.isPrivate)
                        messageBubbleClass += " private-message";
                      return (
                        <React.Fragment key={item._id || item.id}>
                          <div
                            onMouseLeave={handleMouseLeave}
                            className={`Message-Container ${item.isAgent ? "agent-message" : "user-message"
                              }`}
                            style={{ marginBottom: isV2 ? 0 : "-2px" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: item.isAgent
                                  ? "flex-end"
                                  : "flex-start",
                              }}
                            >
                              {/* Pre-chat form only for very first message in chat */}

                              {(item.file_image || item.content) &&
                                (isLastInGroup ? (
                                  <div
                                    style={{
                                      maxWidth:
                                        isFirstMessageOverall && !item.isAgent
                                          ? isV2
                                            ? "480px"
                                            : "360px"
                                          : undefined,
                                      minWidth:
                                        isV2 &&
                                        isFirstMessageOverall &&
                                        !item.isAgent
                                          ? "280px"
                                          : undefined,
                                    }}
                                    className={`${messageBubbleClass}${
                                      isV2 && isFirstMessageOverall && !item.isAgent
                                        ? " chatv2-prechat"
                                        : ""
                                    }`}
                                    onMouseEnter={() => handleMouseEnter(idx)}
                                  >
                                    {isFirstMessageOverall && !item.isAgent && (
                                      <p
                                        className="First-Message-Heading"
                                        style={{ width: "100%" }}
                                      >
                                        Pre-chat form
                                      </p>
                                    )}
                                    {item.file_image && (
                                      <ChatAttachmentImage
                                        src={item.file_image}
                                        onOpen={openFullScreen}
                                        fullWidth={isV2}
                                      />
                                    )}

                                    {/* Fullscreen Modal */}
                                    {isModalOpen && (
                                      <div className="modal">
                                        <span
                                          onClick={closeFullScreen}
                                          className="modal-close"
                                        >
                                          &times;
                                        </span>
                                        <img
                                          src={modalImageSrc}
                                          className="modal-image"
                                          alt="Fullscreen"
                                        />
                                      </div>
                                    )}

                                    {/* display content */}
                                    {item.content && (
                                      <div className="message-text-container">
                                        <div
                                          className={
                                            item.isPrivate
                                              ? "Private-Message-Content"
                                              : isFirstMessageOverall &&
                                                !item.isAgent
                                                ? "First-Message-Content"
                                                : "Message-Content"
                                          }
                                        >
                                          <p
                                            className={
                                              isFirstMessageOverall &&
                                                !item.isAgent
                                                ? "First-Message-Text"
                                                : "Message-Text"
                                            }
                                          >
                                            {isFirstMessageOverall &&
                                              !item.isAgent
                                              ? formatText(item.content)
                                              : item.content}
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <CustomTimeTooltip
                                    time={msgTimeWithSeconds(item.createdAt)}
                                  >
                                    <div
                                      className={messageBubbleClass}
                                      onMouseEnter={() => handleMouseEnter(idx)}
                                    >
                                      {item.file_image && (
                                        <ChatAttachmentImage
                                          src={item.file_image}
                                          onOpen={openFullScreen}
                                          fullWidth={isV2}
                                        />
                                      )}

                                      {/* Fullscreen Modal */}
                                      {isModalOpen && (
                                        <div className="modal">
                                          <span
                                            onClick={closeFullScreen}
                                            className="modal-close"
                                          >
                                            &times;
                                          </span>
                                          <img
                                            src={modalImageSrc}
                                            className="modal-image"
                                            alt="Fullscreen"
                                          />
                                        </div>
                                      )}

                                      {/* display content */}
                                      {item.content && (
                                        <div className="message-text-container">
                                          <div
                                            className={
                                              item.isPrivate
                                                ? "Private-Message-Content"
                                                : isFirstMessageOverall &&
                                                  !item.isAgent
                                                  ? "First-Message-Content"
                                                  : "Message-Content"
                                            }
                                          >
                                            <p
                                              className={
                                                isFirstMessageOverall &&
                                                  !item.isAgent
                                                  ? "First-Message-Text"
                                                  : "Message-Text"
                                              }
                                            >
                                              {isFirstMessageOverall &&
                                                !item.isAgent
                                                ? formatText(item.content)
                                                : item.content}
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </CustomTimeTooltip>
                                ))}
                              {isLastInGroup && (
                              <div
                                style={{ marginTop: isV2 ? "4px" : "5px" }}
                                id={`message-${item._id}`}
                                className={`Message-Container ${item.isAgent
                                  ? "agent-message"
                                  : "user-message"
                                  }`}
                              >
                                <div className="User-message-text-down">
                                  <div className="message-status">
                                        {item.isAgent &&
                                          !item.isPrivate &&
                                          (item.readBy?.length > 0 ||
                                            (item.readBy?.length == 0 &&
                                              item.unreadBy?.length === 0) ? (
                                            <DoubleGreentick className="tick-icon" />
                                          ) : (
                                            <DoubleGreytick className="tick-icon" />
                                          ))}
                                        <span className="User-Messagetime01">
                                          {msgTime(item.createdAt)}
                                        </span>
                                  </div>
                                </div>
                              </div>
                              )}
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}

          {(() => {
            // Until the room's messages arrive there is nothing to base the end
            // reason on, and the generic "closed by an agent" fallback reads as
            // fact rather than a placeholder.
            if (loading) return null;

            const visitorFeedbackText =
              (feedback && String(feedback).trim()) ||
              (rating && String(rating).trim()) ||
              (selectUser?.rating && String(selectUser.rating).trim()) ||
              "";
            const hasVisitorFeedback = Boolean(visitorFeedbackText);
            const chatHasEnded = !!(
              selectUser?.end_session ||
              endChat ||
              selectUser?.endSessionTime ||
              selectUser?.is_archived
            );
            if (!chatHasEnded) return null;

            const endedAt =
              endChatTime ||
              selectUser?.endSessionTime ||
              endRoomTime ||
              "";
            const endReasonText = (() => {
              if (selectUser?.archived_message) {
                return selectUser.archived_message;
              }
              if (selectUser?.endedByRole === "agent") {
                return selectUser.endedBy
                  ? `${selectUser.endedBy} ends the chat`
                  : "This chat has been closed by an agent.";
              }
              if (selectUser?.endedByRole === "visitor" || endChat) {
                const visitorName =
                  selectUser?.endedBy || selectUser.title || "Visitor";
                return `${visitorName} ended the chat`;
              }
              // No speculative agent fallback — socket often marks end_session
              // before archived_message / endedByRole arrive (or before we leave).
              return null;
            })();

            // Active chat just got end_session from the archive socket: skip the
            // whole ended block until a real notice exists (or the view unmounts).
            if (!endReasonText && !selectUser?.is_archived && !endChat) {
              return null;
            }

            const issueResolvedValue = !hasVisitorFeedback
              ? null
              : feedback || rating || endChat
                ? isIssueResolved
                : selectUser?.isIssueResolved;

            return (
              <>
                <div className="Message-Time-Header-Ended">
                  <span className="Left-Line" />
                  <p className="Message-Time-Text">
                    {isV2 ? (
                      <>
                        <span className="cv2-chat-ended-label">Chat Ended:</span>
                        <span className="cv2-chat-ended-time">
                          {msgTime(endedAt)}
                        </span>
                      </>
                    ) : (
                      <>
                        Chat Ended:
                        {msgTime(endedAt)}
                      </>
                    )}
                  </p>
                  <span className="right-Line" />
                </div>
                {endReasonText ? (
                  <div className="Left-Chat-Container">
                    <Logout />
                    <p className="Left-Chat-Text">{endReasonText}</p>
                  </div>
                ) : null}
                {hasVisitorFeedback && (
                  <>
                    <div className="Chat-Comment-Container">
                      <p className="Chat-Comment">
                        {selectUser.title} left the following comment:
                      </p>
                      <p className="Chat-Comment-Text">
                        "{visitorFeedbackText}"
                      </p>
                    </div>
                    {issueResolvedValue !== null &&
                      issueResolvedValue !== undefined && (
                        <div className="Chat-Comment-Container">
                          <p className="Chat-Comment">
                            Was your issue resolved during this chat?
                          </p>
                          <p className="Chat-Comment-Text">
                            "{issueResolvedValue ? "Yes" : "No"}"
                          </p>
                        </div>
                      )}
                  </>
                )}
              </>
            );
          })()}

          {typingIndicator ? (
            <TypingIndicator
              avatar={selectUser?.customer_image}
              bgColor={
                colorCodesForAvatar[
                chracterPosition(
                  selectUser?.title?.substring(0, 1)?.toUpperCase()
                )
                ]
              }
              letter={selectUser?.title?.substring(0, 1)?.toUpperCase()}
            />
          ) : (
            ""
          )}
        </div>
      </div>
      {!selectUser.is_archived &&
        !selectUser.is_unAssigned &&
        !selectUser.end_session &&
        !endChat ? (
        <>
          {isV2 && (
            <div className="ChatMessage-Footer-Container">
              <ChatMessageFooter
                key={`${selectUser.id}-tags`}
                isPrivate={isPrivate}
                setIsPrivate={setIsPrivate}
                roomId={selectUser.id}
                tags={selectUser.tags}
                setSelectedUser={setSelectUser}
                variant="v2"
              />
            </div>
          )}
          <div className="ChatMessage_Container_Input">
            <ChatMessageInput
              setCannedResponse={setCannedResponse}
              selectUser={selectUser}
              sendMessage={sendMessage}
              editedMessage={editedMessage}
              handleEditMessage={handleEditMessage}
              setDrafts={setDrafts}
              setInputText={setInputText}
              inputText={inputText}
              fileImage={fileImage}
              setFileImage={setFileImage}
              fileImageSrc={fileImageSrc}
              setFileImageSrc={setFileImageSrc}
              sendMessageHandler={sendMessageHandler}
              isPrivate={isPrivate}
              setIsPrivate={setIsPrivate}
              canSendPublicReply={canSendPublicReply}
              variant={variant}
            />
          </div>
          {!isV2 && (
            <div className="ChatMessage-Footer-Container">
              <ChatMessageFooter
                key={selectUser.id}
                isPrivate={isPrivate}
                setIsPrivate={setIsPrivate}
                roomId={selectUser.id}
                tags={selectUser.tags}
                setSelectedUser={setSelectUser}
              />
            </div>
          )}
        </>
      ) : selectUser.is_unAssigned && !selectUser.is_archived ? (
        <div className="Archive-Info">
          <div className="Archive-Section">
            <p className="Archive-msg">This chat is unassigned</p>
            {!selectUser.endSession && (
              <button
                className="StartChat-Button"
                type="button"
                onClick={handleOpenRoom}
                disabled={isAssigning}
              >
                {isAssigning ? " Assigning..." : "Start Chat"}
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="Archive-Info">
            <div className="Archive-Section">
              <p className="Archive-msg">This chat has been archived.</p>
              {/* {!selectUser.endSession && (
              <button 
                className="OpenChat-Button"
                type="button"
                onClick={handleOpenChat}
              >
                Open Chat
              </button>
            )} */}
            </div>
          </div>
          <div className="ChatMessage-Footer-Container ChatMessage-Footer-Container--archive">
            <ChatMessageFooter
              key={selectUser.id}
              isPrivate={isPrivate}
              setIsPrivate={setIsPrivate}
              roomId={selectUser.id}
              tags={selectUser.tags}
              setSelectedUser={setSelectUser}
              hidePrivateMode
            />
          </div>
        </>
      )}
    </div>
  );
}

export default ChatMessageComponent;
